<template>
    <div v-if="event" class="space-y-4">
        <FlexibleCard headline-id="baseData" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('baseData')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('baseData')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('baseData')"
                    @enter-edit-mode="onEnterEditMode('baseData', $t('base-data'))"
                    @leave-edit-mode="leaveEditMode('baseData')"
                    @discard-changes="discardChanges('baseData')"
                    @save="saveForm('baseData')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('base-data') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventBaseDataForm
                        v-if="showForm('baseData')"
                        ref="eventBaseDataFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('name-short')"
                            :text="valueToString(event.nameShort)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('name-long')"
                            :text="valueToString(event.nameLong)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('subtitle')"
                            :text="valueToString(event.subtitle)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('execution-year')"
                            :text="valueToString(event.executionYear?.toString())"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('description')"
                            :text="valueToString(event.description)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('annual-specifics')"
                            :text="valueToString(event.annualSpecifics)"
                            :has-border-bottom="true"
                        />
                        <CardRowImage
                            :label="$t('logo')"
                            :label-hint="$t('img-hint-bigger-version')"
                            :image-url="logoState === 'Processing' ? (pendingLogoUrl ?? '') : iconUrl"
                            :image-alt="`Logo ${event.nameShort}`"
                            :image-state="logoState"
                            :has-border-bottom="false"
                            :throw-on-image-error="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowImage from '@/components/Card/CardRowImage.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import EventBaseDataForm from '@/event/forms/event/EventBaseDataForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { thumbnailUrl } from '@/utils/file';
import { useFluent } from 'fluent-vue';
import { computed, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const { event, logoState, pendingLogoUrl } = useEventInjection();
const iconUrl = computed(() => thumbnailUrl(event.value?.icon, 'url128'));

const fluent = useFluent();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    getCardStatus,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
} = useActiveCard();

const eventBaseDataFormRef = useTemplateRef('eventBaseDataFormRef');
const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();
register('baseData', eventBaseDataFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
